import { useEffect, useLayoutEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { MicWaveform } from "@andorhealth/clientuireact.designsystem";

/** Who said the line — the patient's own words are tinted to tell them apart. */
export type TranscriptSpeaker = "agent" | "patient";

export type TranscriptLine = {
  /**
   * Globally monotonic. New ids are what drive the slide-in, and trimming is
   * expressed as "drop everything below id X", so ids must never be reused.
   */
  id: number;
  speaker: TranscriptSpeaker;
  text: string;
};

/**
 * Figma 20183:57197 — 24px/32px Poppins Medium in a 635px column, 1px
 * `Neutrals/75` border at 20px radius with 24px padding. The design's box holds
 * exactly four lines of copy, and that is what bounds the transcript.
 */
const LINE_HEIGHT_PX = 32;
const VISIBLE_LINES = 4;
const CONTENT_HEIGHT_PX = LINE_HEIGHT_PX * VISIBLE_LINES;
const CONTENT_WIDTH_PX = 700;

/** Vertical rhythm between two consecutive turns. */
const STACK_GAP_PX = 8;
/** Matches the `transcript-line-in` fade in globals.css. */
const SLIDE_MS = 420;
const SLIDE_EASE = "cubic-bezier(0.22, 0.61, 0.36, 1)";

/** Corner mic visual while the agent is listening. */
const WAVEFORM_SIZE_PX = 50;
/** Bars that fit 100×100 with the design-system defaults (3px bar, 2px gap). */
const WAVEFORM_HISTORY = 20;

const SPEAKER_COLOR: Record<TranscriptSpeaker, string> = {
  // Figma's copy colour.
  agent: "rgba(255, 255, 255, 0.75)",
  // The hint teal, so the patient can see their own words were heard.
  patient: "var(--hint-accent, #3bb8c1)",
};

const textSx = {
  fontFamily: "var(--font-sans)",
  fontWeight: 500,
  fontSize: "24px",
  lineHeight: `${LINE_HEIGHT_PX}px`,
  textAlign: "center" as const,
  textWrap: "pretty" as const,
  // Figma's copy carries its own line break (node 20183:57197).
  whiteSpace: "pre-wrap" as const,
  overflowWrap: "anywhere" as const,
};

type Props = {
  lines: TranscriptLine[];
  /** Shown while there is nothing to transcribe (the Figma resting copy). */
  placeholder?: string;
  /** Shows the design-system mic waveform in the top-left corner. */
  listening?: boolean;
  /** Current mic loudness `0..1`, used to scroll the listening waveform. */
  micLevel?: number;
};

/**
 * The conversation panel: agent and patient turns append at the bottom and the
 * stack rises out of the box, oldest first, once it no longer fits.
 *
 * Growth is animated by starting the stack translated *down* by however much it
 * just grew and easing that back to zero, so the arriving turn rises in from the
 * bottom edge while the older ones slide up together. Doing it from the measured
 * delta (rather than per-line) means a trim in the same commit still reads as
 * one continuous movement.
 */
export function TranscriptBox({
  lines,
  placeholder,
  listening = false,
  micLevel = 0,
}: Props) {
  const boxRef = useRef<HTMLDivElement>(null);
  const stackRef = useRef<HTMLDivElement>(null);
  /** Height the stack had at the previous commit, to measure growth against. */
  const lastHeight = useRef(0);
  /** Growth still owed an animation — survives a trim re-render. */
  const pendingSlide = useRef(0);

  const [oldestVisibleId, setOldestVisibleId] = useState<number | null>(null);
  const [waveformLevels, setWaveformLevels] = useState<number[]>(() =>
    Array(WAVEFORM_HISTORY).fill(0),
  );
  const visible =
    oldestVisibleId === null
      ? lines
      : lines.filter((line) => line.id >= oldestVisibleId);

  // Scroll a short loudness history for MicWaveform's controlled `number[]` API.
  useEffect(() => {
    if (!listening) {
      setWaveformLevels(Array(WAVEFORM_HISTORY).fill(0));
      return;
    }
    const sample = Math.max(0, Math.min(1, micLevel));
    setWaveformLevels((prev) => [...prev.slice(1), sample]);
  }, [listening, micLevel]);

  useLayoutEffect(() => {
    const box = boxRef.current;
    const stack = stackRef.current;
    if (!box || !stack) return;

    const height = stack.offsetHeight;
    const limit = box.clientHeight;

    const grew = height - lastHeight.current;
    if (grew > 0) {
      // Never slide further than the box itself, or a long agent turn would
      // start entirely below the frame and read as a jump.
      pendingSlide.current = Math.min(pendingSlide.current + grew, limit);
    }
    lastHeight.current = height;

    // Drop whole turns off the top until the rest fits the designed box. Always
    // keep the newest one, even if it alone overflows — it is the live reply.
    if (height > limit && visible.length > 1) {
      const turns = Array.from(stack.children) as HTMLElement[];
      let remaining = height;
      let drop = 0;
      while (drop < turns.length - 1 && remaining > limit) {
        remaining -= turns[drop].offsetHeight + STACK_GAP_PX;
        drop += 1;
      }
      if (drop > 0) {
        // Re-runs this effect against the trimmed stack, still before paint;
        // `pendingSlide` carries the growth across so nothing pops.
        setOldestVisibleId(visible[drop].id);
        return;
      }
    }

    const dy = pendingSlide.current;
    pendingSlide.current = 0;
    if (dy <= 0) return;

    stack.style.transition = "none";
    stack.style.transform = `translateY(${dy}px)`;
    // Commit the start frame so the browser animates from it, not to it.
    void stack.offsetHeight;
    stack.style.transition = `transform ${SLIDE_MS}ms ${SLIDE_EASE}`;
    stack.style.transform = "translateY(0)";
  }, [visible]);

  const showPlaceholder = visible.length === 0 && Boolean(placeholder);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        gap: 0.5,
        px: "24px",
        pb: "24px",
        pt: listening ? 0 : "24px",
        border: "1px solid var(--neutrals-75, #d7dada)",
        borderRadius: "20px",
        flexShrink: 0,
        maxWidth: "100%",
      }}
    >
      {listening && (
        <Box
          sx={{
            alignSelf: "center",
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
            flexShrink: 0,
            pointerEvents: "none",
          }}
        >
          <MicWaveform
            source={waveformLevels}
            width={WAVEFORM_SIZE_PX}
            height={WAVEFORM_SIZE_PX}
            mode="history"
            gain={1}
            color="var(--hint-accent, #3bb8c1)"
            aria-label="Microphone listening"
          />
        </Box>
      )}

      <Box
        ref={boxRef}
        aria-live="polite"
        aria-atomic="false"
        sx={{
          width: `${CONTENT_WIDTH_PX}px`,
          maxWidth: "100%",
          height: `${CONTENT_HEIGHT_PX}px`,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          justifyContent: showPlaceholder ? "center" : "flex-end",
        }}
      >
        {showPlaceholder ? (
          <Typography sx={{ ...textSx, color: SPEAKER_COLOR.agent }}>
            {placeholder}
          </Typography>
        ) : (
          <Box
            ref={stackRef}
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: `${STACK_GAP_PX}px`,
              flexShrink: 0,
              willChange: "transform",
            }}
          >
            {visible.map((line) => (
              <Typography
                key={line.id}
                sx={{
                  ...textSx,
                  color: SPEAKER_COLOR[line.speaker],
                  animation: `transcript-line-in ${SLIDE_MS}ms ease-out both`,
                }}
              >
                {line.text}
              </Typography>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
}
